var _dec, _dec2, _class, _ColorContrast;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { error } from '@instructure/console';
import { contrastWithAlpha, validateContrast } from '@instructure/ui-color-utils';
import { withStyle } from '@instructure/emotion';
import { Text } from '@instructure/ui-text';
import { Pill } from '@instructure/ui-pill';
import ColorIndicator from '../ColorIndicator';
import { propTypes, allowedProps } from './props';
import generateStyle from './styles';
import generateComponentTheme from './theme';

/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
let ColorContrast = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_ColorContrast = class ColorContrast extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.renderStatus = (pass, description) => {
      const _this$props = this.props,
        successLabel = _this$props.successLabel,
        failureLabel = _this$props.failureLabel,
        styles = _this$props.styles;
      return _jsxs("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.statusWrapper,
        children: [_jsx("div", {
          css: pass ? styles === null || styles === void 0 ? void 0 : styles.successDescription : styles === null || styles === void 0 ? void 0 : styles.failureDescription,
          children: description
        }), _jsx("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.statusIndicatorWrapper,
          children: _jsx(Pill, {
            color: pass ? 'success' : 'danger',
            children: pass ? successLabel : failureLabel
          })
        })]
      });
    };
    this.renderColorIndicator = (color, label) => {
      var _this$props$styles, _this$props$styles2, _this$props$styles3;
      return _jsxs(_Fragment, {
        children: [_jsx("div", {
          children: _jsx("div", {
            css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.colorIndicator,
            children: _jsx(ColorIndicator, {
              color: color
            })
          })
        }), _jsxs("div", {
          children: [_jsx("div", {
            css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.colorIndicatorLabel,
            children: label
          }), _jsx("div", {
            css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.pickedColorHex,
            children: color
          })]
        })]
      });
    };
    this.state = {
      contrast: 1,
      isValidNormalText: false,
      isValidLargeText: false,
      isValidGraphicsText: false
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
    this.calcState();
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props3, _this$props4, _this$props5, _this$props6;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
    if ((prevProps === null || prevProps === void 0 ? void 0 : prevProps.firstColor) !== ((_this$props4 = this.props) === null || _this$props4 === void 0 ? void 0 : _this$props4.firstColor) || (prevProps === null || prevProps === void 0 ? void 0 : prevProps.secondColor) !== ((_this$props5 = this.props) === null || _this$props5 === void 0 ? void 0 : _this$props5.secondColor) || (prevProps === null || prevProps === void 0 ? void 0 : prevProps.validationLevel) !== ((_this$props6 = this.props) === null || _this$props6 === void 0 ? void 0 : _this$props6.validationLevel)) {
      var _this$props7, _this$props7$onContra;
      const newState = this.calcState();
      (_this$props7 = this.props) === null || _this$props7 === void 0 ? void 0 : (_this$props7$onContra = _this$props7.onContrastChange) === null || _this$props7$onContra === void 0 ? void 0 : _this$props7$onContra.call(_this$props7, {
        contrast: newState.contrast,
        isValidNormalText: newState.isValidNormalText,
        isValidLargeText: newState.isValidLargeText,
        isValidGraphicsText: newState.isValidGraphicsText,
        firstColor: this.props.firstColor,
        secondColor: this.props.secondColor
      });
    }
  }
  renderPreview() {
    const _this$props8 = this.props,
      styles = _this$props8.styles,
      withoutColorPreview = _this$props8.withoutColorPreview,
      firstColor = _this$props8.firstColor,
      secondColor = _this$props8.secondColor,
      firstColorLabel = _this$props8.firstColorLabel,
      secondColorLabel = _this$props8.secondColorLabel;
    if (withoutColorPreview) {
      return null;
    }
    if (!firstColorLabel || !secondColorLabel) {
      error(false, 'When `withoutColorPreview` is not set to true, the properties `firstColorLabel` and `secondColorLabel` are required!');
    }
    return !withoutColorPreview && _jsxs("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.colorPreview,
      children: [_jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.firstColorPreview,
        children: this.renderColorIndicator(firstColor, firstColorLabel || '')
      }), _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.secondColorPreview,
        children: this.renderColorIndicator(secondColor, secondColorLabel || '')
      })]
    });
  }
  calcState() {
    const contrast = contrastWithAlpha(this.props.firstColor, this.props.secondColor);
    const newState = {
      contrast,
      ...validateContrast(contrast, this.props.validationLevel)
    };
    this.setState(newState);
    return newState;
  }
  render() {
    const _this$props9 = this.props,
      styles = _this$props9.styles,
      label = _this$props9.label,
      normalTextLabel = _this$props9.normalTextLabel,
      largeTextLabel = _this$props9.largeTextLabel,
      graphicsTextLabel = _this$props9.graphicsTextLabel;
    const _this$state = this.state,
      contrast = _this$state.contrast,
      isValidNormalText = _this$state.isValidNormalText,
      isValidLargeText = _this$state.isValidLargeText,
      isValidGraphicsText = _this$state.isValidGraphicsText;
    return _jsxs("div", {
      ...omitProps(this.props, ColorContrast.allowedProps),
      ref: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.colorContrast,
      children: [_jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.label,
        children: _jsx(Text, {
          weight: "bold",
          as: "div",
          children: label
        })
      }), _jsxs(Text, {
        size: "x-large",
        children: [contrast, ":1"]
      }), this.renderPreview(), this.renderStatus(isValidNormalText, normalTextLabel), this.renderStatus(isValidLargeText, largeTextLabel), this.renderStatus(isValidGraphicsText, graphicsTextLabel)]
    });
  }
}, _ColorContrast.displayName = "ColorContrast", _ColorContrast.propTypes = propTypes, _ColorContrast.allowedProps = allowedProps, _ColorContrast.componentId = 'ColorContrast', _ColorContrast.defaultProps = {
  withoutColorPreview: false,
  validationLevel: 'AA'
}, _ColorContrast)) || _class) || _class);
export { ColorContrast };
export default ColorContrast;